import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileArchive, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExpenseExportDialog({ isOpen, onClose }) {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(0, 1);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const res = await fetch(`${API_URL}/api/expenses/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Export fehlgeschlagen");
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ivoliq_Belege_${startDate}_${endDate}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success("Export erfolgreich heruntergeladen");
      onClose();
    } catch (e) {
      toast.error(e.message || "Export fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  // Quick period buttons
  const setQuarter = (q) => {
    const y = new Date().getFullYear();
    const quarters = {
      Q1: [`${y}-01-01`, `${y}-03-31`],
      Q2: [`${y}-04-01`, `${y}-06-30`],
      Q3: [`${y}-07-01`, `${y}-09-30`],
      Q4: [`${y}-10-01`, `${y}-12-31`],
    };
    const [s, e] = quarters[q];
    setStartDate(s);
    setEndDate(e);
  };

  const setYear = (y) => {
    setStartDate(`${y}-01-01`);
    setEndDate(`${y}-12-31`);
  };

  const currentYear = new Date().getFullYear();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px]" data-testid="expense-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-emerald-600" />
            Belege & Ausgaben exportieren
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-600">
            ZIP-Datei mit allen Belegen + Excel-Übersicht für deinen Steuerberater.
            Einheitliches Naming: <code className="text-xs bg-slate-100 px-1 rounded">YYYY-MM-DD_Händler_Betrag</code>
          </p>

          {/* Quick period selection */}
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Schnellauswahl</Label>
            <div className="flex flex-wrap gap-1.5">
              {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                <Button key={q} size="sm" variant="outline" className="text-xs h-7 px-2" onClick={() => setQuarter(q)}>
                  {q} {currentYear}
                </Button>
              ))}
              <Button size="sm" variant="outline" className="text-xs h-7 px-2" onClick={() => setYear(currentYear)}>
                {currentYear}
              </Button>
              <Button size="sm" variant="outline" className="text-xs h-7 px-2" onClick={() => setYear(currentYear - 1)}>
                {currentYear - 1}
              </Button>
            </div>
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Von</Label>
              <Input
                data-testid="export-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Bis</Label>
              <Input
                data-testid="export-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
            <p className="font-medium">Enthält:</p>
            <p>• Excel-Übersicht mit allen Ausgabendaten</p>
            <p>• Alle Belegdateien im Ordner <code className="bg-slate-200 px-1 rounded">/Belege/</code></p>
            <p>• Einheitliches Dateinaming für Zuordnung</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleExport} disabled={loading} data-testid="export-download-btn">
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird erstellt...</>
            ) : (
              <><Download className="h-4 w-4 mr-2" /> ZIP herunterladen</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
